import {
  getAllBeneficiariesData,
  getEnrolledBeneficiaries,
} from "@/services/beneficiaries";
import { clearSelectedBeneficiary } from "@/store/beneficiarySlice";
import {
  BeneficiaryType,
  EnrolledBeneficiaryType,
} from "@/types/beneficiaries";
import { isBoardingProcessingStatue } from "@/types/employeeState";
import { formatDate } from "@/utils/generalUtilities";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

export const useBeneficiariesData = (
  employeeID: string,
  userId: string,
  onboardingProgress: string,
) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [beneficiaries, setBeneficiaries] = useState<EnrolledBeneficiaryType[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [bannerMessage, setBannerMessage] = useState("");

  const fetchBeneficiaries = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      dispatch(clearSelectedBeneficiary());

      const [responseFromAccSystemAPI, responseFromEmployeeAPI] =
        await Promise.all([
          getEnrolledBeneficiaries(employeeID),
          getAllBeneficiariesData(employeeID, userId),
        ]);

      if (!responseFromAccSystemAPI?.data || !responseFromEmployeeAPI?.data) {
        setError(t("home.errors.load_beneficiaries_invalid_data"));
        return;
      }

      const formattedBeneficiaries = responseFromAccSystemAPI.data
        .filter((accBeneficiary: EnrolledBeneficiaryType) =>
          responseFromEmployeeAPI.data.some(
            (empBeneficiary: BeneficiaryType) =>
              empBeneficiary.minorAccountID === accBeneficiary.minorAccountId,
          ),
        )
        .map((beneficiary: EnrolledBeneficiaryType) => {
          const employeeAPIMatch = responseFromEmployeeAPI.data.find(
            (item: { minorAccountID: string }) =>
              item.minorAccountID === beneficiary.minorAccountId,
          );

          return {
            ...beneficiary,
            goal: employeeAPIMatch?.goal || "",
            amountRequired: employeeAPIMatch?.amountRequired || 0,
            vestingScheduleName: employeeAPIMatch?.vestingScheduleName || "",
          };
        });

      const totalSavings = formattedBeneficiaries.reduce(
        (sum: number, account: EnrolledBeneficiaryType) =>
          sum + (account.marketValueAsOfDate || 0),
        0,
      );

      if (
        formattedBeneficiaries.length > 0 &&
        formattedBeneficiaries[0]?.vestingScheduleStartDate &&
        totalSavings === 0
      ) {
        setBannerMessage(
          t("home.banner.success_message", {
            date: formatDate(
              formattedBeneficiaries[0].vestingScheduleStartDate,
            ),
          }),
        );
        setShowBanner(true);
      }

      setBeneficiaries(formattedBeneficiaries);
    } catch {
      setError(t("home.errors.load_beneficiaries_general"));
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, employeeID, userId, t]);

  useEffect(() => {
    if (!isBoardingProcessingStatue(onboardingProgress)) {
      navigate("/wizard");
      return;
    }
    fetchBeneficiaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingProgress]);

  const totals = useMemo(() => {
    if (!beneficiaries.length) {
      return {
        totalSavings: 0,
        totalEmployerContributions: 0,
        employerContributions: 0,
        employeeContributions: 0,
      };
    }

    return beneficiaries.reduce(
      (acc, account) => ({
        totalSavings: acc.totalSavings + (account.marketValueAsOfDate || 0),
        totalEmployerContributions:
          acc.totalEmployerContributions +
          (account.actualEmployerContribution || 0) +
          (account.actualUnvestedEmployerContribution || 0),
        employerContributions:
          acc.employerContributions +
          (account.employerContribution || 0) +
          (account.unvestedEmployerContribution || 0),
        employeeContributions:
          acc.employeeContributions + (account.employeeContribution || 0),
      }),
      {
        totalSavings: 0,
        totalEmployerContributions: 0,
        employerContributions: 0,
        employeeContributions: 0,
      },
    );
  }, [beneficiaries]);

  return {
    beneficiaries,
    totals,
    isLoading,
    error,
    showBanner,
    bannerMessage,
    setShowBanner,
  };
};
